import React, { useEffect, useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Alert,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { EligibilityData } from '../../types';
import { getDataProviders, DataProvider } from '../../services/dataProviderService';
import { Dayjs } from 'dayjs';

interface EligibilityFormProps {
    eligibilityData: EligibilityData;
    fieldErrors: { [key: string]: string };
    onSubmit: (e: React.FormEvent) => void;
    onInputChange: (field: keyof EligibilityData, value: any) => void;
    onDateChange: (field: keyof EligibilityData, value: Dayjs | null) => void;
    submitButtonText: string;
    showDeleteButton?: boolean;
    onDelete?: () => void;
    showCancelButton?: boolean;
    onCancel?: () => void;
}

const EligibilityForm: React.FC<EligibilityFormProps> = ({
    eligibilityData,
    fieldErrors,
    onSubmit,
    onInputChange,
    onDateChange,
    submitButtonText,
    showDeleteButton = false,
    onDelete,
    showCancelButton = false,
    onCancel,
}) => {
    const [dataProviders, setDataProviders] = useState<DataProvider[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDataProviders = async () => {
            try {
                const providersData = await getDataProviders();
                setDataProviders(providersData);
            } catch (error) {
                console.error('[EligibilityForm] Error fetching data providers', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDataProviders();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                Loading...
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={onSubmit}>
                {fieldErrors.general && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {fieldErrors.general}
                    </Alert>
                )}
                <Stack spacing={3}>
                    {/* Basic Information Section */}
                    <Typography variant="h6" gutterBottom>
                        Basic Information
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Partner</InputLabel>
                        <Select
                            value={eligibilityData.dataProviderId || ""}
                            label="Partner"
                            onChange={(e) => onInputChange('dataProviderId', Number(e.target.value))}
                            required
                            error={!!fieldErrors.dataProviderId}
                        >
                            {dataProviders.map(provider => (
                                <MenuItem key={provider.id} value={provider.id}>
                                    {provider.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {fieldErrors.dataProviderId && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {fieldErrors.dataProviderId}
                            </Typography>
                        )}
                    </FormControl>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ position: 'relative', width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>Member ID</Typography>
                                <Tooltip title="MemberId is called 'FamilyId' in Four Winds data and 'IdNumber' in MedOne data">
                                    <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                </Tooltip>
                            </Box>
                            <TextField
                                fullWidth
                                label="Member ID"
                                value={eligibilityData.memberId || ""}
                                onChange={(e) => onInputChange('memberId', e.target.value)}
                                required
                                error={!!fieldErrors.memberId}
                                helperText={fieldErrors.memberId}
                            />
                        </Box>
                        <Box sx={{ position: 'relative', width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>Group ID</Typography>
                                <Tooltip title="GroupId is called 'GroupNum' in MedOne data and 'Department' in Four Winds data">
                                    <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                </Tooltip>
                            </Box>
                            <TextField
                                fullWidth
                                label="Group ID"
                                value={eligibilityData.groupId || ""}
                                onChange={(e) => onInputChange('groupId', e.target.value)}
                                required
                                error={!!fieldErrors.groupId}
                                helperText={fieldErrors.groupId}
                            />
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={eligibilityData.firstName || ""}
                            onChange={(e) => onInputChange('firstName', e.target.value)}
                            required
                            error={!!fieldErrors.firstName}
                            helperText={fieldErrors.firstName}
                        />
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={eligibilityData.lastName || ""}
                            onChange={(e) => onInputChange('lastName', e.target.value)}
                            required
                            error={!!fieldErrors.lastName}
                            helperText={fieldErrors.lastName}
                        />
                        <TextField
                            fullWidth
                            label="Middle Name"
                            value={eligibilityData.middleName || ""}
                            onChange={(e) => onInputChange('middleName', e.target.value)}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="SSN"
                            value={eligibilityData.ssn || ""}
                            onChange={(e) => onInputChange('ssn', e.target.value)}
                            required
                            error={!!fieldErrors.ssn}
                            helperText={fieldErrors.ssn}
                        />
                        <FormControl fullWidth>
                            <DatePicker
                                label="Date of Birth"
                                value={eligibilityData.dateOfBirth}
                                onChange={(value) => onDateChange('dateOfBirth', value)}
                            />
                        </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel>Gender</InputLabel>
                            <Select
                                value={eligibilityData.gender || ""}
                                label="Gender"
                                onChange={(e) => onInputChange('gender', e.target.value)}
                                required
                            >
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                                <MenuItem value="Non Binary">Non Binary</MenuItem>
                                <MenuItem value="Not Specified">Not Specified</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Suffix"
                            value={eligibilityData.suffix || ""}
                            onChange={(e) => onInputChange('suffix', e.target.value)}
                        />
                    </Stack>

                    {/* Address Information Section */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Address Information
                    </Typography>

                    <TextField
                        fullWidth
                        label="Street Address 1"
                        value={eligibilityData.streetAddress1 || ""}
                        onChange={(e) => onInputChange('streetAddress1', e.target.value)}
                        required
                        error={!!fieldErrors.streetAddress1}
                        helperText={fieldErrors.streetAddress1}
                    />

                    <TextField
                        fullWidth
                        label="Street Address 2"
                        value={eligibilityData.streetAddress2 || ""}
                        onChange={(e) => onInputChange('streetAddress2', e.target.value)}
                    />

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="City"
                            value={eligibilityData.city || ""}
                            onChange={(e) => onInputChange('city', e.target.value)}
                            required
                            error={!!fieldErrors.city}
                            helperText={fieldErrors.city}
                        />
                        <TextField
                            fullWidth
                            label="State"
                            value={eligibilityData.state || ""}
                            onChange={(e) => onInputChange('state', e.target.value)}
                            required
                            error={!!fieldErrors.state}
                            helperText={fieldErrors.state}
                        />
                        <TextField
                            fullWidth
                            label="ZIP Code"
                            value={eligibilityData.zip || ""}
                            onChange={(e) => onInputChange('zip', e.target.value)}
                            required
                            error={!!fieldErrors.zip}
                            helperText={fieldErrors.zip}
                        />
                    </Stack>

                    {/* Contact Information Section */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Contact Information
                    </Typography>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={eligibilityData.email || ""}
                            onChange={(e) => onInputChange('email', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Preferred Language"
                            value={eligibilityData.preferredLanguage || ""}
                            onChange={(e) => onInputChange('preferredLanguage', e.target.value)}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Home Phone"
                            value={eligibilityData.homePhone || ""}
                            onChange={(e) => onInputChange('homePhone', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Work Phone"
                            value={eligibilityData.workPhone || ""}
                            onChange={(e) => onInputChange('workPhone', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Mobile Phone"
                            value={eligibilityData.mobilePhone || ""}
                            onChange={(e) => onInputChange('mobilePhone', e.target.value)}
                        />
                    </Stack>

                    {/* Coverage Information Section */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Coverage Information
                    </Typography>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Relationship Code"
                            value={eligibilityData.relationshipCode || ""}
                            onChange={(e) => onInputChange('relationshipCode', e.target.value)}
                            required
                            error={!!fieldErrors.relationshipCode}
                            helperText={fieldErrors.relationshipCode}
                        />
                        <TextField
                            fullWidth
                            label="Coverage Tier"
                            value={eligibilityData.coverageTier || ""}
                            onChange={(e) => onInputChange('coverageTier', e.target.value)}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Coverage Type"
                            value={eligibilityData.coverageType || ""}
                            onChange={(e) => onInputChange('coverageType', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Plan Code"
                            value={eligibilityData.planCode || ""}
                            onChange={(e) => onInputChange('planCode', e.target.value)}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <FormControl fullWidth>
                            <DatePicker
                                label="Effective Date"
                                value={eligibilityData.effectiveDate}
                                onChange={(value) => onDateChange('effectiveDate', value)}
                            />
                        </FormControl>
                        <FormControl fullWidth>
                            <DatePicker
                                label="End Date"
                                value={eligibilityData.endDate}
                                onChange={(value) => onDateChange('endDate', value)}
                            />
                        </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel>Active Status</InputLabel>
                            <Select
                                value={eligibilityData.activeStatus || ""}
                                label="Active Status"
                                onChange={(e) => onInputChange('activeStatus', e.target.value)}
                            >
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Inactive">Inactive</MenuItem>
                                <MenuItem value="Terminated">Terminated</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Employment Status</InputLabel>
                            <Select
                                value={eligibilityData.employmentStatus || ""}
                                label="Employment Status"
                                onChange={(e) => onInputChange('employmentStatus', e.target.value)}
                            >
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Retired">Retired</MenuItem>
                                <MenuItem value="Terminated">Terminated</MenuItem>
                                <MenuItem value="Leave of Absence">Leave of Absence</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <FormControl fullWidth>
                            <DatePicker
                                label="Hire Date"
                                value={eligibilityData.hireDate}
                                onChange={(value) => onDateChange('hireDate', value)}
                            />
                        </FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={eligibilityData.primarySubscriber || false}
                                        onChange={(e) => onInputChange('primarySubscriber', e.target.checked)}
                                    />
                                }
                                label="Primary Subscriber"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={eligibilityData.isStudent || false}
                                        onChange={(e) => onInputChange('isStudent', e.target.checked)}
                                    />
                                }
                                label="Is Student"
                            />
                        </Box>
                    </Stack>

                    {/* Additional Fields Section */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Additional Information
                    </Typography>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Alternate ID"
                            value={eligibilityData.alternateId || ""}
                            onChange={(e) => onInputChange('alternateId', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Previous ID"
                            value={eligibilityData.previousId || ""}
                            onChange={(e) => onInputChange('previousId', e.target.value)}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Person Code"
                            value={eligibilityData.personCode || ""}
                            onChange={(e) => onInputChange('personCode', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Location"
                            value={eligibilityData.location || ""}
                            onChange={(e) => onInputChange('location', e.target.value)}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Sub Group Code"
                            value={eligibilityData.subGroupCode || ""}
                            onChange={(e) => onInputChange('subGroupCode', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Chronic Condition"
                            value={eligibilityData.chronicCondition || ""}
                            onChange={(e) => onInputChange('chronicCondition', e.target.value)}
                        />
                    </Stack>

                    {/* Diagnosis Codes */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Diagnosis Code 1"
                            value={eligibilityData.diagnosisCode1 || ""}
                            onChange={(e) => onInputChange('diagnosisCode1', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Diagnosis Code 2"
                            value={eligibilityData.diagnosisCode2 || ""}
                            onChange={(e) => onInputChange('diagnosisCode2', e.target.value)}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Diagnosis Code 3"
                            value={eligibilityData.diagnosisCode3 || ""}
                            onChange={(e) => onInputChange('diagnosisCode3', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Diagnosis Code 4"
                            value={eligibilityData.diagnosisCode4 || ""}
                            onChange={(e) => onInputChange('diagnosisCode4', e.target.value)}
                        />
                    </Stack>

                    {/* Custom Fields */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Client Custom 1"
                            value={eligibilityData.clientCustom1 || ""}
                            onChange={(e) => onInputChange('clientCustom1', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Client Custom 2"
                            value={eligibilityData.clientCustom2 || ""}
                            onChange={(e) => onInputChange('clientCustom2', e.target.value)}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label="Client Custom 3"
                            value={eligibilityData.clientCustom3 || ""}
                            onChange={(e) => onInputChange('clientCustom3', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Client Custom 4"
                            value={eligibilityData.clientCustom4 || ""}
                            onChange={(e) => onInputChange('clientCustom4', e.target.value)}
                        />
                    </Stack>

                    <TextField
                        fullWidth
                        label="Client Custom 5"
                        value={eligibilityData.clientCustom5 || ""}
                        onChange={(e) => onInputChange('clientCustom5', e.target.value)}
                    />

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                        <Button type="submit" variant="contained" fullWidth>
                            {submitButtonText}
                        </Button>

                        {showDeleteButton && onDelete && (
                            <Button 
                                type="button" 
                                variant="contained" 
                                fullWidth 
                                onClick={onDelete} 
                                color="error"
                            >
                                Delete Record
                            </Button>
                        )}

                        {showCancelButton && onCancel && (
                            <Button 
                                type="button" 
                                variant="outlined" 
                                fullWidth 
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </form>
        </LocalizationProvider>
    );
};

export default EligibilityForm;
